import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, FormControlLabel, Switch, Divider, ToggleButton, ToggleButtonGroup,
    FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { InstitutionService } from '../../services/institution.service';
import { InstitutionSettingsService } from '../../services/institutionSettings.service';
import { IInstitution, NoShiftPunchPolicy } from '../../interfaces/institution.interface';
import { WeekDay } from '../../interfaces/institutionSettings.interface';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

// day: index into common:weekdaysShort (0 = Sunday)
const WEEK_DAYS: { value: WeekDay; day: 0 | 1 | 2 | 3 | 4 | 5 | 6 }[] = [
    { value: 'MONDAY', day: 1 },
    { value: 'TUESDAY', day: 2 },
    { value: 'WEDNESDAY', day: 3 },
    { value: 'THURSDAY', day: 4 },
    { value: 'FRIDAY', day: 5 },
    { value: 'SATURDAY', day: 6 },
    { value: 'SUNDAY', day: 0 },
];

const InstitutionDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['institutions', 'common']);
    const toast = useToast();
    const [institution, setInstitution] = useState<IInstitution | null>(null);
    const [isAttendanceButtonEnabled, setIsAttendanceButtonEnabled] = useState(true);
    const [weekendDays, setWeekendDays] = useState<WeekDay[]>(['FRIDAY', 'SATURDAY']);
    const [noShiftPunchPolicy, setNoShiftPunchPolicy] = useState<NoShiftPunchPolicy>('LEGACY_ROW');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (id) {
            loadData();
        }
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const inst = await InstitutionService.getInstitutionById(id!);
            setInstitution(inst);
            setNoShiftPunchPolicy(inst.noShiftPunchPolicy ?? 'LEGACY_ROW');

            const settings = await InstitutionSettingsService.getSettings(id!);
            if (settings) {
                setIsAttendanceButtonEnabled(settings.isAttendanceButtonEnabled);
                if (settings.weekendDays && settings.weekendDays.length > 0) {
                    setWeekendDays(settings.weekendDays);
                }
            }
        } catch (error) {
            toast.showApiError(error, t('detail.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        if (weekendDays.length === 0) {
            toast.showError(t('shared.selectOneWeekendDay'));
            return;
        }
        if (weekendDays.length >= 7) {
            toast.showError(t('shared.weekendNotAllDays'));
            return;
        }
        try {
            setSaving(true);
            if (institution) {
                await InstitutionService.updateInstitution(id!, {
                    name: institution.name,
                    code: institution.code,
                    description: institution.description,
                    email: institution.email,
                    fiscalYearStartMonth: institution.fiscalYearStartMonth,
                    noShiftPunchPolicy,
                });
            }
            await InstitutionSettingsService.updateSettings(id!, { isAttendanceButtonEnabled });
            await InstitutionSettingsService.updateWeekendDays(id!, weekendDays);
            toast.showSuccess(t('shared.settingsSaved'));
        } catch (error) {
            toast.showApiError(error, t('shared.saveSettingsFailed'));
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ p: 3 }}><Typography>{t('shared.loading')}</Typography></Box>;
    }

    if (!institution) {
        return <Box sx={{ p: 3 }}><Typography>{t('detail.notFound')}</Typography></Box>;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Button startIcon={<ArrowBack />} onClick={() => navigate('/institutions')}>
                    {t('common:actions.back')}
                </Button>
                <Typography variant="h5">{institution.name}</Typography>
            </Box>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('detail.info')}</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                    <Box>
                        <Typography variant="body2" color="text.secondary">{t('shared.fields.name')}</Typography>
                        <Typography>{institution.name}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">{t('shared.fields.code')}</Typography>
                        <Typography>{institution.code}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">{t('shared.fields.description')}</Typography>
                        <Typography>{institution.description || '-'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">{t('shared.fields.adminEmail')}</Typography>
                        <Typography>{institution.email || '-'}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">{t('detail.fiscalYearStartMonth')}</Typography>
                        <Typography>{institution.fiscalYearStartMonth}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="body2" color="text.secondary">{t('shared.fields.status')}</Typography>
                        <Typography>
                            {!institution.approved ? t('shared.status.notApproved') : institution.isActive === false ? t('shared.status.deactivated') : t('shared.status.active')}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{t('detail.settings')}</Typography>
                <Divider sx={{ mb: 2 }} />

                <FormControlLabel
                    control={
                        <Switch
                            checked={isAttendanceButtonEnabled}
                            onChange={(e) => setIsAttendanceButtonEnabled(e.target.checked)}
                        />
                    }
                    label={t('detail.attendanceButtons')}
                    sx={{ mb: 3, display: 'block' }}
                />

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>{t('detail.weekendDays')}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        {t('shared.weekendHelp')}
                    </Typography>
                    <ToggleButtonGroup
                        value={weekendDays}
                        onChange={(_, next: WeekDay[]) => setWeekendDays(next)}
                        aria-label={t('shared.weekendDaysAria')}
                        size="small"
                    >
                        {WEEK_DAYS.map(d => (
                            <ToggleButton key={d.value} value={d.value} aria-label={d.value}>
                                {t(`common:weekdaysShort.${d.day}`)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <Box sx={{ mb: 3, maxWidth: 360 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="no-shift-policy-label">{t('detail.noShiftPolicy')}</InputLabel>
                        <Select
                            labelId="no-shift-policy-label"
                            label={t('detail.noShiftPolicy')}
                            value={noShiftPunchPolicy}
                            onChange={(e) => setNoShiftPunchPolicy(e.target.value as NoShiftPunchPolicy)}
                        >
                            <MenuItem value="LEGACY_ROW">{t('detail.policies.LEGACY_ROW')}</MenuItem>
                            <MenuItem value="DEFAULT_SHIFT">{t('detail.policies.DEFAULT_SHIFT')}</MenuItem>
                            <MenuItem value="ORPHAN_BLOCK">{t('detail.policies.ORPHAN_BLOCK')}</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {t('detail.noShiftPolicyHelp')}
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveSettings}
                    disabled={saving}
                >
                    {saving ? t('shared.saving') : t('shared.saveSettings')}
                </Button>
            </Paper>
        </Box>
    );
};

export default InstitutionDetail;
